import { z } from 'zod';

import { AiRequestBaseSchema } from './ai-request';

/**
 * One category a classification may assign.
 *
 * The description is not decoration — it is the entire definition the model
 * works from, and for chat moderation it comes from the staff-authored violation
 * type (P20), so an operator retunes the classifier by editing text rather than
 * by shipping a prompt.
 */
export const AiClassificationLabelSchema = z
  .object({
    key: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/, 'A label key is lower_snake_case.'),
    description: z.string().min(1).max(2_000),
  })
  .strict();

export type AiClassificationLabel = z.infer<typeof AiClassificationLabelSchema>;

/**
 * Label-and-confidence classification — the `classify` half of the provider
 * interface, served by the `ai-text` capability.
 *
 * The adapter constrains the vendor to a JSON schema derived from `labels`, so
 * the answer is machine-checkable rather than prose the platform has to parse
 * hopefully. A response that does not satisfy it is an `invalid_response`
 * refusal, never a low-confidence guess.
 */
export const AiClassificationRequestSchema = AiRequestBaseSchema.extend({
  /** Optional extra framing (house rules, locale notes). */
  system: z.string().min(1).max(100_000).optional(),
  /** The text being classified. */
  input: z.string().min(1).max(100_000),
  /**
   * The categories to score. Non-empty and bounded: an unbounded label set is a
   * prompt of unbounded size, priced per token.
   */
  labels: z.array(AiClassificationLabelSchema).min(1).max(50),
  /**
   * Whether the model must explain itself.
   *
   * On for anything a human later reviews or a member can appeal (N20): a
   * moderation decision with no rationale is one a reviewer cannot check.
   */
  includeReason: z.boolean().optional(),
  maxOutputTokens: z.number().int().min(1).max(8_000),
}).strict();

export type AiClassificationRequest = z.infer<typeof AiClassificationRequestSchema>;

export const AiClassificationScoreSchema = z
  .object({
    key: z.string().min(1),
    confidence: z.number().min(0).max(1),
  })
  .strict();

export type AiClassificationScore = z.infer<typeof AiClassificationScoreSchema>;

/**
 * A score for **every** requested label, not only the ones that matched.
 *
 * A sparse result cannot be distinguished from a truncated one, and a caller
 * comparing against `moderation.ai.confidenceThreshold` needs the number it is
 * comparing to exist. The platform verifies the coverage before returning.
 */
export const AiClassificationResultSchema = z
  .object({
    scores: z.array(AiClassificationScoreSchema),
    /** Present only when `includeReason` was set. */
    reason: z.string().max(4_000).nullable(),
  })
  .strict();

export type AiClassificationResult = z.infer<typeof AiClassificationResultSchema>;
